import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Dropdown, Input } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { ReactComponent as DownArrowSvg } from '@assets/icons/arrow-down.svg';
import { ReactComponent as SortSvg } from '@assets/icons/sort-arrow.svg';
import { ReactComponent as SearchSvg } from '@assets/icons/search.svg';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { useResponsive } from '@hooks/useResponsive';

import './FilterSortPanel.less';

const DEFAULT_SORT = 'Date created';

type FilterSortPanelProps = {
  sortMenuText?: string;
  orderBy?: string;
  sortMenu: ItemType[];
  onSortHandler?(key: string): void;
  dropdownMenuText?: string;
  dropdownMenu: ItemType[];
  onDropdownHandler?(key: string): void;
  searchQuery?: string | null;
  onSearchOpenChange?(open: boolean): void;
  onSearchHandler?(value: string): void;
};

function FilterSortPanel({
  sortMenuText = 'Sort',
  orderBy,
  sortMenu,
  onSortHandler,
  dropdownMenuText = 'All items',
  dropdownMenu,
  onDropdownHandler,
  searchQuery,
  onSearchOpenChange,
  onSearchHandler
}: FilterSortPanelProps) {
  const [isShowSearch, setIsShowSearch] = useState<boolean>(
    searchQuery !== undefined && searchQuery !== null
  );
  const [sortSelected, setSortSelected] = useState<string>(
    orderBy ?? DEFAULT_SORT
  );
  const [dropdownMenuSelected, setDropdownMenuSelected] = useState<string>('');
  const { isMdWidth } = useResponsive();

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (onSearchHandler) {
      onSearchHandler(value);
    }
  };
  const closeSearch = () => {
    setIsShowSearch(false);
    if (onSearchHandler) onSearchHandler('');
    if (onSearchOpenChange) onSearchOpenChange(false);
  };

  const updateItems = (arr: ItemType[]) => {
    if (isMdWidth) {
      const newArr = arr.map((item: any) =>
        item.children
          ? {
              ...item,
              children: [
                ...item.children,
                {
                  label: 'Cancel',
                  key: 'submenu-cancel'
                }
              ]
            }
          : item
      );
      return [
        ...newArr,
        {
          label: 'Cancel',
          key: 'cancel'
        }
      ];
    }
    return arr;
  };

  const searchValue = searchQuery === undefined ? undefined : searchQuery ?? '';

  useEffect(() => {
    const exist = sortMenu?.some((i) => i?.key === sortSelected);
    if (!exist && sortSelected !== DEFAULT_SORT) {
      onSortHandler?.(DEFAULT_SORT);
      setSortSelected(DEFAULT_SORT);
    }
  }, [sortSelected, sortMenu, onSortHandler, setSortSelected]);

  return (
    <div className="filter-sort-panel">
      {isShowSearch && (
        <div className="filter-sort-panel-search">
          <Input
            allowClear={{
              clearIcon: (
                <CrossSvg
                  onClick={(e) => {
                    e.stopPropagation();
                    closeSearch();
                  }}
                />
              )
            }}
            value={searchValue}
            onBlur={(e) => {
              if (e.target.value) return;
              closeSearch();
            }}
            onChange={(e) => {
              if (searchValue === e.target.value) return;
              onSearch(e);
            }}
            autoFocus={true}
            prefix={<SearchSvg />}
            className="filter-sort-panel-search-input"
            placeholder="Search comment"
          />
        </div>
      )}
      <Row gutter={8} wrap={false} justify="space-between">
        <Col>
          <Dropdown
            menu={{
              selectable: true,
              selectedKeys: [sortSelected],
              onClick: ({ key }: { key: string }) => {
                if (onSortHandler && key.indexOf('cancel') < 0) {
                  onSortHandler(key);
                  setSortSelected(key);
                }
              },
              items: updateItems(sortMenu)
            }}
            prefixCls="media_viewer_menu"
            overlayClassName="media_viewer_mobile-overlay"
            placement="bottomRight"
            trigger={['click']}
          >
            <Button className="media_viewer_btn filter-sort-panel-sort">
              {sortMenuText}
              <SortSvg className="b-white-icon" />
            </Button>
          </Dropdown>
        </Col>
        <Col>
          <Dropdown
            menu={{
              selectedKeys: [
                dropdownMenuSelected || dropdownMenu?.[0]?.key || ('' as any)
              ],
              onClick: ({ key }: { key: string }) => {
                if (onDropdownHandler && key.indexOf('cancel') < 0) {
                  onDropdownHandler(key);
                  setDropdownMenuSelected(key);
                }
              },
              items: updateItems(dropdownMenu),
              selectable: true
            }}
            prefixCls="media_viewer_menu"
            overlayClassName="media_viewer_mobile-overlay"
            placement="bottom"
            trigger={['click']}
          >
            <Button className="media_viewer_btn filter-sort-panel-comments">
              {dropdownMenuText}
              <DownArrowSvg className="b-white-icon" />
            </Button>
          </Dropdown>
        </Col>
        <Col flex="auto">
          <Button
            htmlType="button"
            onClick={() => {
              setIsShowSearch(true);
              if (onSearchOpenChange) onSearchOpenChange(true);
            }}
            className="media_viewer_btn b-only-icon"
          >
            <SearchSvg className="b-white-icon" />
          </Button>
        </Col>
      </Row>
    </div>
  );
}

export default FilterSortPanel;
